import { ChangeDetectionStrategy, Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EditorBodyAdapter } from '@app/utils/interfaces/editor-interface';
import { OfferProductItem, OfferSectionData } from '@app/utils/interfaces/offer.interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-offers-editor-body',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './offers-editor-body.component.html',
  styleUrl: './offers-editor-body.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OffersEditorBodyComponent implements OnInit, EditorBodyAdapter<OfferSectionData> {
  @Input({ required: true }) public data!: OfferSectionData;
  @Input() public isPreviewMode = false;

  private readonly fb = inject(FormBuilder);

  protected readonly selectedProducts = signal<OfferProductItem[]>([]);

  protected readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    isVisible: [true],
    selectionType: [1, [Validators.required]],
    searchQuery: [''],
  });

  public ngOnInit(): void {
    this.form.patchValue({
      name: this.data.name ?? '',
      isVisible: this.data.isVisible,
      selectionType: this.data.selectionType ?? 1,
      searchQuery: '',
    });

    const initialProducts = (this.data.inventoryItemIds ?? []).map((id: string, index: number) => ({
      id,
      name: `Selected product ${index + 1}`,
    }));

    this.selectedProducts.set(initialProducts);
  }

  public isValid(): boolean {
    return this.form.valid && this.selectedProducts().length > 0;
  }

  public markAllAsTouched(): void {
    this.form.markAllAsTouched();
  }

  public hasChanges(): boolean {
    const value = this.form.getRawValue();
    const currentIds = this.selectedProducts().map((item: OfferProductItem) => item.id);

    return (
      value.name !== (this.data.name ?? '') ||
      value.isVisible !== this.data.isVisible ||
      value.selectionType !== (this.data.selectionType ?? 1) ||
      JSON.stringify(currentIds) !== JSON.stringify(this.data.inventoryItemIds ?? [])
    );
  }

  public canPreview(): boolean {
    return Boolean(this.form.controls.name.value.trim()) && this.selectedProducts().length > 0;
  }

  public getVisibilityValue(): boolean {
    return this.form.controls.isVisible.value;
  }

  public setVisibilityValue(value: boolean): void {
    this.form.controls.isVisible.setValue(value);
  }

  public getSubmitPayload(): OfferSectionData {
    const value = this.form.getRawValue();

    return {
      name: value.name.trim(),
      isVisible: value.isVisible,
      selectionType: value.selectionType,
      inventoryItemIds: this.selectedProducts().map((item: OfferProductItem) => item.id),
    };
  }

  protected removeProduct(id: string): void {
    this.selectedProducts.update((items: OfferProductItem[]) =>
      items.filter((item: OfferProductItem) => item.id !== id),
    );
  }

  protected addManualProduct(): void {
    const query = this.form.controls.searchQuery.value.trim();

    if (!query) {
      return;
    }

    const normalizedId = query.toLowerCase().replace(/\s+/g, '-');

    const exists = this.selectedProducts().some(
      (item: OfferProductItem) => item.id === normalizedId || item.name === query,
    );

    if (exists) {
      return;
    }

    this.selectedProducts.update((items: OfferProductItem[]) => [
      ...items,
      {
        id: normalizedId,
        name: query,
      },
    ]);

    this.form.controls.searchQuery.setValue('');
  }
}
